import React, { useState } from 'react'
import { useCoachDashboard } from './CoachDashboardProvider'
import { InputField, SelectField } from '~/components/shared/FormFields'
import { Form } from '~/lib/ui/form'
import moment from 'moment'
import { cn } from '~/lib/utils'
import Tooltip from '../_atoms/Tooltip/index'
import Button from '../_atoms/Button/index'
import YnsIcon from '../icons/YnsIcon'
import CancelMeetingModal from './CancelMeetingModal'

const programLevelStyles = {
  Flex: {
    bg: 'bg-willow-brook-100',
    text: 'text-intoo-green-coachcta',
  },
  Premium: {
    bg: 'bg-intoo-green-coachcta',
    text: 'text-willow-brook-100',
  },
  Select: {
    bg: 'bg-cloud-burst',
    text: 'text-white',
  },
}

const MeetingHeader = ({ meeting }) => {
  const styles =
    programLevelStyles[meeting.candidate_program_level] ||
    programLevelStyles.Flex

  return (
    <div className="flex justify-between">
      <div>
        <div className="text-base font-bold">{meeting.service_request} for</div>
        <a
          href={meeting.candidate_url}
          className="font-medium text-primary !underline"
        >
          {meeting.candidate}
        </a>
      </div>
      <div
        className={cn(
          'inline-flex h-8 items-center rounded-2xl px-4 py-2 font-medium',
          styles.bg,
          styles.text,
        )}
      >
        <YnsIcon name="tag" className="mr-2 h-5 w-5" />
        <span className="font-medium">{meeting.candidate_program_level}</span>
      </div>
    </div>
  )
}

const MeetingCard = ({ meeting, onCancelClick }) => {
  return (
    <div className="mb-4 flex min-h-40 flex-col justify-between rounded-xl border p-4">
      <MeetingHeader meeting={meeting} />
      <div className="my-4 space-y-2 text-sm">
        <div className="text-sm font-bold"> Scheduled Meeting:</div>
        <p className="!text-sm">
          {moment(meeting.meeting_time).format('dddd MMMM D [at] h:mm A z')}
        </p>
      </div>
      <div className="mb-2">
        <Button
          href={meeting.join_url}
          target="_blank"
          rel="noopener noreferrer"
          variant="solid"
          color="primary"
          size="sm"
        >
          Join Meeting
        </Button>
      </div>
      <div>
        <button
          type="button"
          className="cursor-pointer border-none bg-transparent p-0 !text-sm text-primary hover:underline"
          onClick={() => onCancelClick(meeting)}
        >
          Cancel & Message Candidate
        </button>
      </div>
    </div>
  )
}

const ActionRequiredCard = ({ action }) => {
  const isPastDue = new Date(action.due_at) < new Date()
  return (
    <div className="mb-4 flex min-h-40 flex-col justify-between rounded-xl border p-4">
      <MeetingHeader meeting={action} />
      <div className="my-4 space-y-2">
        <p className="!text-sm">
          <span className="font-bold">Created:</span>{' '}
          {moment(action.created_at).format('LL')}
        </p>
        <p className="!text-sm">
          <span className="font-bold">Task:</span> {action.coach_next_task}
        </p>
        <p className="!text-sm">
          <span className="font-bold">Due At:</span>{' '}
          <span className={cn('', isPastDue ? 'text-red-500' : '')}>
            {moment(action.due_at).format('L')}{' '}
            {moment(action.due_at).format('LT')}{' '}
          </span>
        </p>
      </div>
      <div className="text-right">
        <a
          href={action.service_request_url}
          className="cursor-pointer border-none bg-transparent p-0 !text-sm text-primary hover:underline"
        >
          View Progress
        </a>
      </div>
    </div>
  )
}

const PastDueTaskCard = ({ task }) => {
  return (
    <div className="mb-4 flex min-h-40 flex-col justify-between rounded-xl border p-4">
      <MeetingHeader meeting={task} />
      <div className="my-4 space-y-2">
        <p className="!text-sm text-red-500">
          <span className="font-bold">Due Date:</span>{' '}
          <span>
            {moment(task.due_at).format('L')}{' '}
            {moment(task.due_at).format('LT')}{' '}
          </span>
        </p>
        <p className="!text-sm">
          <span className="font-bold">Task:</span> {task.coach_next_task}
        </p>
      </div>
      <div className="text-right">
        <a
          href={task.service_request_url}
          className="cursor-pointer border-none bg-transparent p-0 !text-sm text-primary hover:underline"
        >
          Update Progress
        </a>
      </div>
    </div>
  )
}

const TodaysActions = () => {
  const { dashboardData, form, handleCancelMeeting } = useCoachDashboard()
  const viewByOptions = ['Today', 'Tomorrow', 'Next 3 Days', 'Next 7 Days']

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedMeeting, setSelectedMeeting] = useState(null)

  const handleCancelClick = (meeting) => {
    setSelectedMeeting(meeting)
    setModalOpen(true)
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setSelectedMeeting(null)
  }

  const handleCancelConfirm = (meeting, message) => {
    handleCancelMeeting(meeting.service_request_id, message)
    setModalOpen(false)
    setSelectedMeeting(null)
  }

  return (
    <div className="space-y-4">
      <Form {...form}>
        <form className="flex space-x-4">
          <InputField
            name="candidateSearch"
            control={form.control}
            className="w-64"
            label="Search Candidate:"
          />
          <SelectField
            name="viewBy"
            control={form.control}
            label="View By:"
            choices={viewByOptions.map((option) => ({
              value: option,
              label: option,
            }))}
            className="w-64"
          />
        </form>
      </Form>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {/* Upcoming Meetings */}
        <div className="rounded-xl border border-silver-400 p-4">
          <div className="subtitle mb-4 flex items-center gap-2">
            Upcoming Meetings
            <Tooltip
              content="Upcoming Meetings include all of your scheduled meetings and events. They are ordered by time and date."
              ariaLabel="Upcoming Meetings Info"
              className="w-64"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                viewBox="0 0 16 16"
                aria-label="Info"
              >
                <circle cx="8" cy="8" r="8" fill="#e5e7eb" />
                <text
                  x="8"
                  y="12"
                  textAnchor="middle"
                  fontSize="10"
                  fill="#374151"
                  fontFamily="Arial"
                  fontWeight="bold"
                >
                  i
                </text>
              </svg>
            </Tooltip>
          </div>
          {dashboardData.meetings.length === 0 ? (
            <div className="text-sm">
              You&apos;re all set! You have no meetings or events for the
              selected time frame.
            </div>
          ) : (
            dashboardData.meetings.map((meeting, index) => (
              <MeetingCard
                meeting={meeting}
                key={index}
                onCancelClick={handleCancelClick}
              />
            ))
          )}
        </div>

        {/* Tasks Due */}
        <div className="rounded-xl border border-silver-400 p-4">
          <div className="subtitle mb-4 flex items-center gap-2">
            Tasks Due
            <Tooltip
              content="Tasks Due includes all of your pending tasks and services, listed by time and date first, and then by program level."
              ariaLabel="Upcoming Meetings Info"
              className="w-64"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                viewBox="0 0 16 16"
                aria-label="Info"
              >
                <circle cx="8" cy="8" r="8" fill="#e5e7eb" />
                <text
                  x="8"
                  y="12"
                  textAnchor="middle"
                  fontSize="10"
                  fill="#374151"
                  fontFamily="Arial"
                  fontWeight="bold"
                >
                  i
                </text>
              </svg>
            </Tooltip>
          </div>
          {dashboardData.actionsRequired.length === 0 ? (
            <div className="text-sm">
              You&apos;re all set! You have no tasks due for the selected time
              frame.
            </div>
          ) : (
            dashboardData.actionsRequired.map((action, index) => (
              <ActionRequiredCard action={action} key={index} />
            ))
          )}
        </div>

        {/* Past Due Tasks */}
        <div className="rounded-xl border border-silver-400 p-4">
          <div className="subtitle mb-4 flex items-center gap-2">
            Past Due Tasks
            <Tooltip
              content="Past Due Tasks includes all tasks that had a due date in the past, but still don't have a completed record. They are ordered by time and date, and then by program level."
              ariaLabel="Upcoming Meetings Info"
              className="w-64"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                viewBox="0 0 16 16"
                aria-label="Info"
              >
                <circle cx="8" cy="8" r="8" fill="#e5e7eb" />
                <text
                  x="8"
                  y="12"
                  textAnchor="middle"
                  fontSize="10"
                  fill="#374151"
                  fontFamily="Arial"
                  fontWeight="bold"
                >
                  i
                </text>
              </svg>
            </Tooltip>
          </div>
          {dashboardData.pastDueTasks.length === 0 ? (
            <div className="text-sm">Nice! You&apos;re all caught up!</div>
          ) : (
            dashboardData.pastDueTasks.map((task, index) => (
              <PastDueTaskCard task={task} key={index} />
            ))
          )}
        </div>
      </div>
      {/* Cancel & Message Modal */}
      <CancelMeetingModal
        open={modalOpen}
        meeting={selectedMeeting}
        onClose={handleModalClose}
        onConfirm={handleCancelConfirm}
      />
    </div>
  )
}

export default TodaysActions
